import { useTranslation } from 'next-i18next';
import React, { ReactElement, ReactNode, useMemo } from 'react';
import { BsLink } from '@hotelplan/core.basis.bs-link';
import { dayMappings } from '@hotelplan/fdr.lib.misc.schedule';
import {
  openMethod,
  routerLink,
} from '@hotelplan/fdr.lib.router.target-page-data';
import { FdrImg } from '@hotelplan/fdr.regular.basis.fdr-img';
import {
  FdrAgencyAddress,
  FdrAgencySpecialHours,
  FdrCoordinates,
  FdrImage,
  FdrLink,
  FdrOpeningHours,
  FdrWorkingDay,
} from '@hotelplan/supergraph-api';
import { FdrSchedule } from 'components/candidate/fdr-schedule';
import { Container } from 'components/domain/container';
import {
  useFdrGetLinkUrlWithJotform,
  useFdrGetLinkWithJotform,
} from 'components/domain/fdr-jotform';
import { FdrScalableImage } from 'components/domain/fdr-scalable-image';
import { FlaglineText, SecondaryHeading } from 'theme/headings';
import {
  FdrAgencyContactContentBlock,
  FdrAgencyContactHeadings,
  FdrAgencyContactIcon,
  FdrAgencyContactModuleWrapper,
  FdrAgencyIconsBlock,
  FdrAgencyImage,
  FdrAgencyScheduleWrapper,
} from './fdr-agency-detail.style';
import { getLocationLink, mapPhoneToTelLink } from './fdr-agency-detail.utils';
import { FdrSpecialHoursBlock } from './fdr-agency-special-hours';

interface IFdrAgencyContactModuleProps {
  name: string;
  flagline: string;
  address: FdrAgencyAddress;
  coordinates: FdrCoordinates;
  image?: FdrImage;
  phone?: string;
  appointmentLink?: FdrLink;
  formLink?: FdrLink;
  openingHours: FdrOpeningHours;
  specialHours?: Array<FdrAgencySpecialHours>;
  timezone: string;
  hasWheelchairAccessibleSeating?: boolean;
}

export function FdrAgencyContactModule({
  flagline,
  name,
  image,
  coordinates,
  address,
  phone,
  appointmentLink,
  formLink,
  openingHours,
  specialHours,
  timezone,
  hasWheelchairAccessibleSeating,
}: IFdrAgencyContactModuleProps): ReactElement {
  const [t] = useTranslation('agency');

  const formLinkLinkWithJotform = useFdrGetLinkWithJotform(formLink);
  const appointmentLinkWithJotform = useFdrGetLinkUrlWithJotform(
    appointmentLink.url
  );

  const { street, city, zip } = address || {};
  const withImage = Boolean(image);
  const locationLink = coordinates ? getLocationLink(coordinates) : undefined;

  const showSchedule = useMemo(() => {
    return (
      openingHours &&
      dayMappings.some(([, day]) => {
        const workingDay = openingHours[day] as FdrWorkingDay;
        return workingDay && workingDay.shifts && workingDay.shifts.length > 0;
      })
    );
  }, [openingHours]);

  return (
    <FdrAgencyContactModuleWrapper>
      <Container>
        <FdrAgencyContactHeadings>
          <FlaglineText
            className="contacts-flagline"
            dangerouslySetInnerHTML={{ __html: flagline }}
          />
          <SecondaryHeading className="contacts-name">{name}</SecondaryHeading>
        </FdrAgencyContactHeadings>
        <FdrAgencyContactContentBlock withImage={withImage}>
          {image ? (
            <FdrAgencyImage>
              <FdrScalableImage
                img={<FdrImg image={image} />}
                className="agency-image"
              />
            </FdrAgencyImage>
          ) : null}
          <FdrAgencyIconsBlock withImage={withImage}>
            <FdrAgencyContactBase
              href={locationLink}
              target="_blank"
              iconName="contact-place"
              text={
                <>
                  <p>{street}</p>
                  <span>{`${zip} ${city}`}</span>
                </>
              }
            />
            <FdrAgencyContactBase
              text={phone}
              href={mapPhoneToTelLink(phone)}
              iconName="contact-phone"
            />
            <FdrAgencyContactBase
              href={routerLink(formLinkLinkWithJotform)?.href as string}
              target={openMethod(formLinkLinkWithJotform)}
              text={t('agency:contacts.request')}
              iconName="contact-email"
            />
            {appointmentLink ? (
              <FdrAgencyContactBase
                href={appointmentLinkWithJotform}
                target={openMethod(appointmentLink)}
                text={t('agency:contacts.appointmentText')}
                iconName="contact-calendar"
              />
            ) : null}
            {hasWheelchairAccessibleSeating ? (
              <FdrAgencyContactBase
                text={t('agency:contacts.accessibilityText')}
                iconName="accessibility"
                className="small"
              />
            ) : null}
          </FdrAgencyIconsBlock>
          {showSchedule ? (
            <FdrAgencyScheduleWrapper>
              <FdrSchedule
                showStatusIcon
                openingHours={openingHours}
                timezone={timezone}
                className="agency-schedule"
              />
              {specialHours?.length ? (
                <FdrSpecialHoursBlock specialHours={specialHours} />
              ) : null}
            </FdrAgencyScheduleWrapper>
          ) : null}
        </FdrAgencyContactContentBlock>
      </Container>
    </FdrAgencyContactModuleWrapper>
  );
}

interface IFdrAgencyContactBaseProps {
  href?: string;
  text: ReactNode | string;
  iconName: string;
  target?: string;
  className?: string;
}

function FdrAgencyContactBase({
  href,
  text,
  target,
  iconName,
  className,
}: IFdrAgencyContactBaseProps): ReactElement {
  return (
    <>
      {href ? (
        <BsLink href={href} target={target || '_self'}>
          <div className="agency-icon-row">
            <FdrAgencyContactIcon name={iconName} variant={null} />
            <span className="agency-icon-text">{text}</span>
          </div>
        </BsLink>
      ) : (
        <div className={`agency-icon-row ${className}`}>
          <FdrAgencyContactIcon name={iconName} variant={className} />
          <span className="agency-icon-text">{text}</span>
        </div>
      )}
    </>
  );
}
